import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import CustomInput from './CustomInput';
import { ReactComponent as Doctor } from '../../../../../assets/img/pharmacy/DOCTOR.svg';
import { ReactComponent as MedicalCommission } from '../../../../../assets/img/pharmacy/medicalCommission.svg';
import { ReactComponent as Recipe } from '../../../../../assets/img/pharmacy/recipe.svg';
import { ReactComponent as Stamp } from '../../../../../assets/img/pharmacy/stamp.svg';
import { ReactComponent as Stamp1 } from '../../../../../assets/img/pharmacy/Stamp1.svg';
import { ReactComponent as Stamp2 } from '../../../../../assets/img/pharmacy/Stamp2.svg';
import { ReactComponent as Stamp4 } from '../../../../../assets/img/pharmacy/Stamp4.svg';
import { ReactComponent as Special1 } from '../../../../../assets/img/pharmacy/Special1.svg';
import { ReactComponent as Special2 } from '../../../../../assets/img/pharmacy/Special2.svg';
import { ReactComponent as Special5 } from '../../../../../assets/img/pharmacy/Special5.svg';
import { ReactComponent as Recipe1 } from '../../../../../assets/img/pharmacy/Recipe1.svg';
import { ReactComponent as Doctor1 } from '../../../../../assets/img/pharmacy/Doctor1.svg';


interface Form14804Props {
  onInputChange?: (id: number, value: string) => void;
  initialValues: InitialValuesType;
  onCheckboxStateChange?: (checkboxStates: {
    inputs: { [key: number]: boolean };
    svgs: { [key: number]: boolean };
  }) => void;
  isShow?: boolean;
  isReadOnly?: boolean;
  isShowDeleteButton?: boolean;
  formDroppedItems?: SvgType[];
  handleDragStart?: (index: number, type: string) => void,
  handleDragOver?: () => void,
  handleDrop?: () => void,
  onSvgDelete?: (index: number, id: number) => void,
  isConstructor?: boolean
  formCheckboxStates
  isChatPage?: boolean
}

interface SvgType {
  id: number;
  type: string;
  x: number;
  y: number;
  isWrongAnswer: boolean;
}

interface InitialValuesType {
  inputs?: {
    [key: number]: {
      value: string | undefined;
      right?: boolean;
      [key: string]: any;
    };
  };
  svgs?: Array<SvgType>;
  [key: number]: string | number | boolean | null;
}



const Form14804 = forwardRef(({
  initialValues,
  onInputChange,
  formDroppedItems = [],
  handleDragStart,
  handleDragOver,
  handleDrop,
  onSvgDelete,
  isShow,
  isReadOnly,
  onCheckboxStateChange,
  isConstructor,
  isChatPage
}: Form14804Props, ref) => {
  
  const [inputCheckboxStates, setInputCheckboxStates] = useState<{ [key: number]: boolean }>(() => {
    if (initialValues?.inputs) {
      const initialCheckboxStates = Object.fromEntries(
        Object.entries(initialValues.inputs).map(([id, input]) => [
          Number(id),
          input.right || false,
        ])
      );
      return initialCheckboxStates;
    }
    return {};
  });

  const [svgCheckboxStates, setSvgCheckboxStates] = useState<{ [key: number]: boolean }>({});
  const [values, setValues] = useState(() => ({
    inputs: initialValues?.inputs || {},
  }));

  useEffect(() => {
    setValues((prevValues) => ({
      inputs: {
        ...prevValues.inputs,
        ...initialValues.inputs,
      },
    }));
  }, [initialValues.inputs]);

  useEffect(() => {
    if (initialValues?.svgs) {
      const initialSvgCheckboxStates = Object.fromEntries(
        initialValues.svgs.map((svg) => [svg.id, svg.isWrongAnswer || false])
      );
      setSvgCheckboxStates(initialSvgCheckboxStates);
    }
  }, [initialValues?.svgs]);

  const resetAllFields = () => {
    setValues({ inputs: {} });
    setInputCheckboxStates({});
    setSvgCheckboxStates({});
  };

  useImperativeHandle(ref, () => ({
    resetAllFields,
  }));

  const handleInputChangeLocal = (id: number, value: string) => {
    setValues((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [id]: {
          ...prev.inputs[id],
          value,
        },
      },
    }));
    onInputChange?.(id, value);
  };

  const handleInputCheckboxChange = (id: number) => {
    setInputCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      setValues((prevValues) => {
        return {
          inputs: {
            ...prevValues.inputs,
            [id]: {
              ...prevValues.inputs[id],
              right: newState[id],
            },
          },
        };
      });

      onCheckboxStateChange?.({ inputs: newState, svgs: svgCheckboxStates });
      return newState;
    });
  };

  const handleSvgCheckboxChange = (id: number) => {
    setSvgCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      onCheckboxStateChange?.({ inputs: inputCheckboxStates, svgs: newState });
      return newState;
    });
  };

  const getSvgStyle = (type: string, x: number, y: number) => {
    const baseStyle = {
      position: 'absolute' as const,
      cursor: 'move'
    };

    switch (type) {
      case 'Doctor':
        return {
          ...baseStyle,
          left: `${x + 20}px`,
          top: `${y + 40}px`,
          transform: 'scale(1)',
        };
      case 'MedicalCommission':
        return {
          ...baseStyle,
          left: `${x - 210}px`,
          top: `${y + 40}px`,
          transform: 'scale(1.1)',
        };
      case 'Recipe':
        return {
          ...baseStyle,
          left: `${x + 40}px`,
          top: `${y + 0}px`,
          transform: 'scale(1)',
        };
      case 'Stamp':
        return {
          ...baseStyle,
          left: `${x - 160}px`,
          top: `${y - 30}px`,
          transform: 'scale(1.2)',
        };
      case 'Stamp1':
        return {
          ...baseStyle,
          left: `${x - 190}px`,
          top: `${y + 5}px`,
          transform: 'scale(1)',
        };
      case 'Stamp2':
        return {
          ...baseStyle,
          left: `${x - 250}px`,
          top: `${y - 88}px`,
          transform: 'scale(1.3)',
        };
      case 'Stamp4':
        return {
          ...baseStyle,
          left: `${x - 165}px`,
          top: `${y - 65}px`,
          transform: 'scale(0.5)',
        };
      case 'Special1':
        return {
          ...baseStyle,
          left: `${x - 70}px`,
          top: `${y - 60}px`,
          transform: 'scale(0.6)',
        };
      case 'Special2':
        return {
          ...baseStyle,
          left: `${x - 40}px`,
          top: `${y - 40}px`,
          transform: 'scale(0.75)',
        };
      case 'Special5':
        return {
          ...baseStyle,
          left: `${x -40}px`,
          top: `${y -50}px`,
          transform: 'scale(0.5)',
        };
      case 'Recipe1':
        return {
          ...baseStyle,
          left: `${x + 175}px`,
          top: `${y - 90}px`,
          transform: 'scale(1)',
        };
      case 'Doctor1':
        return {
          ...baseStyle,
          left: `${x + 110}px`,
          top: `${y - 100}px`,
          transform: 'scale(1.1)',
        };
      default:
        return {
          ...baseStyle,
          left: `${x}px`,
          top: `${y}px`,
          transform: 'scale(1)',
        };
    }
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      paddingBottom: 50,
      paddingTop: 30,
      border: '1px solid rgba(34, 34, 34, 0.31)',
      padding: 20,
      position: 'relative'
    }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 40 }}>
        <span>ФОРМА РЕЦЕПТУРНОГО БЛАНКА N 148-1/У-04(Л)</span>
      </div>
      {[...initialValues.svgs || [], ...formDroppedItems].map((svg, index) => (
        <div
          key={`svg-${svg.id}-${index}`}
          className="svg-wrapper"
          style={getSvgStyle(svg.type, svg.x, svg.y)}
          draggable={isConstructor}
          onDragStart={() => handleDragStart?.(index, 'response')}
        >
          {svg.type === 'Doctor' && <Doctor />}
          {svg.type === 'MedicalCommission' && <MedicalCommission />}
          {svg.type === 'Recipe' && <Recipe />}
          {svg.type === 'Stamp' && <Stamp />}
          {svg.type === 'Stamp1' && <Stamp1 />}
          {svg.type === 'Stamp2' && <Stamp2 />}
          {svg.type === 'Stamp4' && <Stamp4 />}
          {svg.type === 'Special1' && <Special1 />}
          {svg.type === 'Special2' && <Special2 />}
          {svg.type === 'Special5' && <Special5 />}
          {svg.type === 'Recipe1' && <Recipe1 />}
          {svg.type === 'Doctor1' && <Doctor1 />}
          <div className="svg-controls">
            {isShow && (
              <input
                type="checkbox"
                className="svg-checkbox"
                checked={svgCheckboxStates[svg.id] || false}
                onChange={() => handleSvgCheckboxChange(svg.id)}
              />
            )}
            {isConstructor && (
              <button
                className="svg-delete-button"
                onClick={() => onSvgDelete?.(index, svg.id)}
              >
                ×
              </button>
            )}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ width: 250 }}>
          <span>Министерство здравоохранения Российской Федерации</span>
        </div>
        <div style={{ display: 'flex', gap: 20, width: 300 }}>
          <span>МЕСТО ДЛЯ ШТРИХКОДА {'<*>'}</span>
          <span>
            УТВЕРЖДЕНА приказом Министерства здравоохранения Российской Федерации от 24 ноября 2021 г. N 1094н
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>Штамп код</span>
        <CustomInput
          id={1}
          onChange={handleInputChangeLocal}
          inputCount={5}
          value={values.inputs?.[1]?.value ?? ''}
          disabled={isReadOnly}
        />
        {isShow && (
          <input
            style={{marginLeft: 20 }}
            type="checkbox"
            id="checkbox-1"
            checked={inputCheckboxStates[1] || false}
            onChange={() => handleInputCheckboxChange(1)}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', marginBottom: 20 }}>
        <span>медицинской организации</span>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <CustomInput
            id={2}
            onChange={handleInputChangeLocal}
            inputCount={15}
            value={values.inputs?.[2]?.value ?? ''}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              style={{ marginLeft: 20 }}
              type="checkbox"
              id="checkbox-2"
              checked={inputCheckboxStates[2] || false}
              onChange={() => handleInputCheckboxChange(2)}
            />
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span>Штамп код</span>
        <CustomInput
          id={1}
          onChange={handleInputChangeLocal}
          inputCount={5}
          value={values.inputs?.[1]?.value ?? ''}
          disabled={isReadOnly}
        />
        {isShow && (
          <input
            style={{  marginLeft: 20 }}
            type="checkbox"
            id="checkbox-39"
            checked={inputCheckboxStates[39] || false}
            onChange={() => handleInputCheckboxChange(39)}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', marginBottom: 20 }}>
        <span>индивидуального предпринимателя</span>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <CustomInput
            id={3}
            onChange={handleInputChangeLocal}
            inputCount={15}
            value={values.inputs?.[3]?.value ?? ''}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              style={{  marginLeft: 20 }}
              type="checkbox"
              id="checkbox-3"
              checked={inputCheckboxStates[3] || false}
              onChange={() => handleInputCheckboxChange(3)}
            />
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
        <span>Код формы по ОКУД 3108805</span>
      </div>
      <span>Форма N 148-1/у-04(л)</span>
      <div style={{ display: 'flex', border: '1px solid black', flexDirection: 'row' }}>
        <div style={{ borderRight: '1px solid black', padding: 10 }}>
          <div>
            <span>Код категории граждан</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
            <CustomInput
              id={4}
              onChange={handleInputChangeLocal}
              inputCount={3}
              value={values.inputs?.[4]?.value ?? ''}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-4"
                checked={inputCheckboxStates[4] || false}
                onChange={() => handleInputCheckboxChange(4)}
              />
            )}
          </div>
        </div>
        <div style={{ borderRight: '1px solid black', padding: 10 }}>
          <div>
            <span>Код нозологической формы (по МКБ)</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
            <CustomInput
              id={5}
              onChange={handleInputChangeLocal}
              inputCount={5}
              value={values.inputs?.[5]?.value ?? ''}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-5"
                checked={inputCheckboxStates[5] || false}
                onChange={() => handleInputCheckboxChange(5)}
              />
            )}
          </div>
        </div>
        <div style={{ borderRight: '1px solid black', padding: 10 }}>
          <div>
            <span>Код нозологической формы (по МКБ)</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>1. Федеральный бюджет</span>
            <span>2. Бюджет субъекта Российской Федерации</span>
            <span>3. Муниципальный бюджет</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 10 }}>
          <span style={{ marginBottom: 20 }}>% оплаты: (подчеркнуть)</span>
          <span>1. Бесплатно</span>
          <span>2. 50%</span>
          <span>3. иной %</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 20, fontWeight: 500, marginTop: 20 }}>РЕЦЕПТ</div>
        <div style={{ display: 'flex', justifyContent: 'row', alignItems: 'flex-end' }}>
          <span>Серия</span>
          <input
            type="text"
            style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none', width: 40, fontSize: '14pt', height: '25px' }}
            value={values.inputs?.[6]?.value ?? ''}
            onChange={(event) => {
              handleInputChangeLocal(6, event.target.value);
            }}
            disabled={isReadOnly}
          />
          <span>N</span>
          <input
            type="text"
            style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none', fontSize: '14pt', height: '25px' }}
            value={values.inputs?.[7]?.value ?? ''}
            onChange={(event) => {
              handleInputChangeLocal(7, event.target.value);
            }}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              style={{  marginLeft: 20 }}
              type="checkbox"
              id="checkbox-7"
              checked={inputCheckboxStates[7] || false}
              onChange={() => handleInputCheckboxChange(7)}
            />
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <span>Дата оформления:</span>
          <div style={{ marginRight: 20 }}>
            <CustomInput
              id={8}
              onChange={handleInputChangeLocal}
              inputCount={2}
              value={values.inputs?.[8]?.value ?? ''}
              disabled={isReadOnly}
            />
          </div>
          <div>
            <CustomInput
              id={9}
              onChange={handleInputChangeLocal}
              inputCount={2}
              value={values.inputs?.[9]?.value ?? ''}
              disabled={isReadOnly}
            />
          </div>
          <span>20</span>
          <input
            style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none', width: 30, fontSize: '14pt' }}
            value={values.inputs?.[10]?.value ?? ''}
            onChange={(event) => {
              handleInputChangeLocal(10, event.target.value);
            }}
            disabled={isReadOnly}
          />
          <span>г.</span>
          {isShow && (
            <input
              style={{  marginLeft: 20 }}
              type="checkbox"
              id="checkbox-10"
              checked={inputCheckboxStates[10] || false}
              onChange={() => handleInputCheckboxChange(10)}
            />
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 25, flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <div style={{ display: 'flex', alignItems: 'center', width: 600, flexDirection: 'row' }}>
            <span>
              Фамилия, инициалы имени и отчества (последнее - при наличии) пациента
              <input
                style={{ width: 500, borderLeft: 'none', borderRight: 'none', borderTop: 'none', fontSize: '14pt', height: '25px' }}
                type="text"
                value={values.inputs?.[11]?.value ?? ''}
                onChange={(event) => {
                  handleInputChangeLocal(11, event.target.value);
                }}
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  style={{  marginLeft: 20 }}
                  type="checkbox"
                  id="checkbox-11"
                  checked={inputCheckboxStates[11] || false}
                  onChange={() => handleInputCheckboxChange(11)}
                />
              )}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', width: 600, flexDirection: 'row' }}>
            <span> Дата рождения</span>
            <div style={{ display: 'flex', flexDirection: 'row', marginRight: 10 }}>
              <CustomInput
                id={11}
                onChange={handleInputChangeLocal}
                inputCount={2}
                value={values.inputs?.[11]?.value ?? ''}
                disabled={isReadOnly}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', marginRight: 10 }}>
              <CustomInput
                id={12}
                onChange={handleInputChangeLocal}
                inputCount={2}
                value={values.inputs?.[12]?.value ?? ''}
                disabled={isReadOnly}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', marginRight: 10 }}>
              <CustomInput
                id={13}
                onChange={handleInputChangeLocal}
                inputCount={4}
                value={values.inputs?.[13]?.value ?? ''}
                disabled={isReadOnly}
              />
            </div>
            {isShow && (
              <input
                style={{  marginLeft: 5 }}
                type="checkbox"
                id="checkbox-13"
                checked={inputCheckboxStates[13] || false}
                onChange={() => handleInputCheckboxChange(13)}
              />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', width: '95%', justifyContent: 'space-between', marginLeft: 20, alignItems: 'flex-end' }}>
          <div style={{ alignSelf: 'flex-end' }}>СНИЛС</div>
          <CustomInput
            id={14}
            onChange={handleInputChangeLocal}
            inputCount={20}
            value={values.inputs?.[14]?.value ?? ''}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              style={{  marginLeft: 20 }}
              type="checkbox"
              id="checkbox-14"
              checked={inputCheckboxStates[14] || false}
              onChange={() => handleInputCheckboxChange(14)}
            />
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', width: '95%', justifyContent: 'flex-start', marginLeft: 20 }}>
          <div style={{ alignSelf: 'flex-end', width: 200, padding: 0 }}>N полиса обязательного медицинского страхования:</div>
          <CustomInput
            id={15}
            onChange={handleInputChangeLocal}
            inputCount={25}
            value={values.inputs?.[15]?.value ?? ''}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              style={{ width: 28 }}
              type="checkbox"
              id="checkbox-15"
              checked={inputCheckboxStates[15] || false}
              onChange={() => handleInputCheckboxChange(15)}
            />
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', padding: 20 }}>
        <span>
          Номер медицинской карты пациента, получающего медицинскую помощь в амбулаторных условиях
        </span>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <input
            style={{ width: '100%', borderLeft: 'none', borderRight: 'none', borderTop: 'none', fontSize: '14pt', height: '25px' }}
            value={values.inputs?.[16]?.value ?? ''}
            onChange={(event) => {
              handleInputChangeLocal(16, event.target.value);
            }}
            type="text"
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              style={{ marginLeft: 20 }}
              type="checkbox"
              id="checkbox-16"
              checked={inputCheckboxStates[16] || false}
              onChange={() => handleInputCheckboxChange(16)}
            />
          )}
        </div>

      </div>
      <div style={{ display: 'flex', flexDirection: 'row', padding: 20 }}>
        <span>
          Фамилия, инициалы имени и отчества лечащего врача (фельдшера, акушерки)
          <input
            style={{ width: '100%', borderLeft: 'none', borderRight: 'none', borderTop: 'none', fontSize: '14pt', height: '25px' }}
            value={values.inputs?.[17]?.value ?? ''}
            onChange={(event) => {
              handleInputChangeLocal(17, event.target.value);
            }}
            type="text"
            disabled={isReadOnly}
          />
        </span>
        {isShow && (
          <input
            style={{  marginLeft: 20 }}
            type="checkbox"
            id="checkbox-17"
            checked={inputCheckboxStates[17] || false}
            onChange={() => handleInputCheckboxChange(17)}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: 20, width: '100%', marginBottom: 40 }}>
        <span>Руб.</span>
        <span>Коп.</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
          <span>
            RP:
            <input
              style={{
                borderLeft: 'none',
                borderTop: 'none',
                borderRight: 'none',
                borderBottom: '1px dotted black',
                width: '90%',
                fontSize: '14pt',
                height: '25px'
              }}
              value={values.inputs?.[18]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(18, event.target.value);
              }}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-18"
                checked={inputCheckboxStates[18] || false}
                onChange={() => handleInputCheckboxChange(18)}
              />
            )}
          </span>
          <span>
            D.t.d.
            <input
              style={{
                borderLeft: 'none',
                borderTop: 'none',
                borderRight: 'none',
                borderBottom: '1px dotted black',
                width: '90%',
                fontSize: '14pt',
                height: '25px'
              }}
              value={values.inputs?.[19]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(19, event.target.value);
              }}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-19"
                checked={inputCheckboxStates[19] || false}
                onChange={() => handleInputCheckboxChange(19)}
              />
            )}
          </span>
          <span>
            Signa:
            <input
              style={{
                borderLeft: 'none',
                borderTop: 'none',
                borderRight: 'none',
                borderBottom: '1px dotted black',
                width: '90%',
                fontSize: '14pt',
                height: '25px'
              }}
              value={values.inputs?.[20]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(20, event.target.value);
              }}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-20"
                checked={inputCheckboxStates[20] || false}
                onChange={() => handleInputCheckboxChange(20)}
              />
            )}
          </span>
        </div>
      </div>
      <div style={{ borderBottom: '1px solid black', marginBottom: 35 }}></div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40 }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <span>Подпись и печать лечащего врача</span>
          <span>(подпись фельдшера, акушерки)</span>
        </div>
        <span style={{ width: '50%' }}>М.П.</span>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        {/* <span>Рецепт действителен в течение 15 дней, 30 дней, 90 дней (нужное подчеркнуть)</span> */}
      </div>
      <div style={{ textAlign: 'center', marginBottom: 10 }}>
        <span>------------------- (Заполняется специалистом аптечной организации) -------------------</span>
      </div>
      <div style={{ display: 'flex', width: '80%', margin: '0 auto', border: '1px solid black' }}>
        <div style={{ borderRight: '1px solid black', width: '100%' }}>
          <div style={{ borderBottom: '1px solid black', padding: 10, display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'flex-start', height: 90 }}>
            <span>Отпущено по рецепту:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[21]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(21, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-21"
                checked={inputCheckboxStates[21] || false}
                onChange={() => handleInputCheckboxChange(21)}
              />
            )}
          </div>
          <div style={{ borderBottom: '1px solid black', padding: 10, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'flex-start', height: 60 }}>
              <span style={{ marginRight: 5 }}>Дата отпуска:</span>
              <span>"</span>
              <input
                style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt', width: 50 }}
                value={values.inputs?.[22]?.value ?? ''}
                onChange={(event) => {
                  const val = event.target.value.replace(/\\D/g, '').slice(0, 2);
                  handleInputChangeLocal(22, val);
                }}
                type="text"
                disabled={isReadOnly}
              />
              <span style={{ marginRight: 15 }}>"</span>
              <input
                style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', width: 120, height: 25, fontSize: '14pt' }}
                value={values.inputs?.[38]?.value ?? ''}
                onChange={(event) => {
                  const val = event.target.value.replace(/\\D/g, '').slice(0, 8);
                  handleInputChangeLocal(38, val);
                }}
                type="text"
                disabled={isReadOnly}
              />
              <span>20</span>
              <input
                style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', width: 50, height: 25, fontSize: '14pt' }}
                value={values.inputs?.[23]?.value ?? ''}
                onChange={(event) => {
                  const val = event.target.value.replace(/\\D/g, '').slice(0, 2);
                  handleInputChangeLocal(23, val);
                }}
                type="text"
                disabled={isReadOnly}
              />
              <span>г.</span>
              {isShow && (
                <input
                  style={{  marginLeft: 20 }}
                  type="checkbox"
                  id="checkbox-24"
                  checked={inputCheckboxStates[24] || false}
                  onChange={() => handleInputCheckboxChange(24)}
                />
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'flex-start', marginTop: 15, marginLeft: 10 }}>
            Приготовил:
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[28]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(28, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-28"
                checked={inputCheckboxStates[28] || false}
                onChange={() => handleInputCheckboxChange(28)}
              />
            )}
          </div>
        </div>
        <div style={{ width: '50%', padding: 10 }}>
          <span>Торговое наименование и дозировка:</span>
          <div style={{ borderBottom: '1px solid black', padding: 10, display: 'flex', flexDirection: 'row' }}>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', width: '100%', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[26]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(26, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20, width: 28 }}
                type="checkbox"
                id="checkbox-26"
                checked={inputCheckboxStates[26] || false}
                onChange={() => handleInputCheckboxChange(26)}
              />
            )}
          </div>
          <div style={{ borderBottom: '1px solid black', padding: '10px 0' }}>
            <span>Количество:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', width: '85%', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[27]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(27, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-27"
                checked={inputCheckboxStates[27] || false}
                onChange={() => handleInputCheckboxChange(27)}
              />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'flex-start', marginTop: 15 }}>
              Проверил:
              <input
                style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', width: '90px', height: 25, fontSize: '14pt' }}
                value={values.inputs?.[29]?.value ?? ''}
                onChange={(event) => {
                  handleInputChangeLocal(29, event.target.value);
                }}
                type="text"
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  style={{  marginLeft: 20 }}
                  type="checkbox"
                  id="checkbox-29"
                  checked={inputCheckboxStates[29] || false}
                  onChange={() => handleInputCheckboxChange(29)}
                />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'flex-start', marginTop: 15, marginLeft: 10 }}>
              Отпустил:
              <input
                style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', width: '90px', height: 25, fontSize: '14pt' }}
                value={values.inputs?.[30]?.value ?? ''}
                onChange={(event) => {
                  handleInputChangeLocal(30, event.target.value);
                }}
                type="text"
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  style={{  marginLeft: 20 }}
                  type="checkbox"
                  id="checkbox-30"
                  checked={inputCheckboxStates[30] || false}
                  onChange={() => handleInputCheckboxChange(30)}
                />
              )}
            </div>

          </div>
        </div>

      </div>
      <span style={{ display: 'flex', justifyContent: 'center', margin: '25px auto', width: '100%' }}>
        --------------------------------------- (линия отрыва) ---------------------------------------
      </span>
      <div style={{ display: 'flex', width: '80%', margin: '0 auto', border: '1px solid black' }}>
        <div style={{ display: 'flex', flexDirection: 'column', borderRight: '1px solid black', padding: 10, width: '100%' }}>
          <div>
            <span>Корешок рецептурного бланка:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt', width: 150 }}
              value={values.inputs?.[31]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(31, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-31"
                checked={inputCheckboxStates[31] || false}
                onChange={() => handleInputCheckboxChange(31)}
              />
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginTop: 20 }}>
            <div>Наименование лекарственного препарата:</div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
              <input
                style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', width: '400px', height: 25, fontSize: '14pt' }}
                value={values.inputs?.[32]?.value ?? ''}
                onChange={(event) => {
                  handleInputChangeLocal(32, event.target.value);
                }}
                type="text"
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  style={{  marginLeft: 10 }}
                  type="checkbox"
                  id="checkbox-32"
                  checked={inputCheckboxStates[32] || false}
                  onChange={() => handleInputCheckboxChange(32)}
                />
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
            <span>Дозировка:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt', width: 310 }}
              value={values.inputs?.[33]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(33, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 10 }}
                type="checkbox"
                id="checkbox-33"
                checked={inputCheckboxStates[33] || false}
                onChange={() => handleInputCheckboxChange(33)}
              />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', padding: 10, width: '100%' }}>
          <div>
            <span>Способ применения:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[34]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(34, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-34"
                checked={inputCheckboxStates[34] || false}
                onChange={() => handleInputCheckboxChange(34)}
              />
            )}
          </div>
          <div>
            <span>Продолжительность:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt', width: 170 }}
              value={values.inputs?.[35]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(35, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            <span>дней</span>
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-35"
                checked={inputCheckboxStates[35] || false}
                onChange={() => handleInputCheckboxChange(35)}
              />
            )}
          </div>
          <div>
            <span>Количество приемов в день:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt', width: 150 }}
              value={values.inputs?.[36]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(36, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            <span>раз</span>
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-36"
                checked={inputCheckboxStates[36] || false}
                onChange={() => handleInputCheckboxChange(36)}
              />
            )}
          </div>
          <div>
            <span>На 1 прием:</span>
            <input
              style={{ borderLeft: 'none', borderTop: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[37]?.value ?? ''}
              onChange={(event) => {
                handleInputChangeLocal(37, event.target.value);
              }}
              type="text"
              disabled={isReadOnly}
            />
            <span>ед</span>
            {isShow && (
              <input
                style={{  marginLeft: 20 }}
                type="checkbox"
                id="checkbox-37"
                checked={inputCheckboxStates[37] || false}
                onChange={() => handleInputCheckboxChange(37)}
              />
            )}
          </div>
        </div>
      </div>
      <span style={{ display: 'flex', justifyContent: 'center', margin: '25px auto', width: '100%' }}>
        {'<*>'}В случае изготовления рецептурного бланка с использованием компьютерных технологий.
      </span>
    </div>
  );

});

export default Form14804;